import React, { memo, useMemo, useCallback, useState } from "react";
import { Crown, Trash2 } from "lucide-react";
import UserCard from "./UserCard";
import UserSearchAdd from "./UserSearchAdd";
import { Button } from "../ui/button";
import EmptyState from "../generic/EmptyState";

const SponsorSection = memo(
  ({
    sponsor,
    isEditMode = false,
    selectedSponsors = [],
    onSelectionChange,
    onDeleteSponsors,
    onAddSponsors,
  }) => {
    const [newSponsorsToAdd, setNewSponsorsToAdd] = useState([]);

    const handleUserSelection = useCallback(
      (user, isSelected) => {
        if (isSelected) {
          onSelectionChange?.([...selectedSponsors, user]);
        } else {
          onSelectionChange?.(
            selectedSponsors.filter(
              (selected) => selected.uid !== user.uid && selected.id !== user.id
            )
          );
        }
      },
      [selectedSponsors, onSelectionChange]
    );

    const isUserSelected = useCallback(
      (user) => {
        return selectedSponsors.some(
          (selected) => selected.uid === user.uid || selected.id === user.id
        );
      },
      [selectedSponsors]
    );

    const handleDeleteSelected = useCallback(() => {
      onDeleteSponsors?.(selectedSponsors);
    }, [onDeleteSponsors, selectedSponsors]);

    const handleAddNewSponsors = useCallback(
      (sponsors) => {
        onAddSponsors?.(sponsors);
        setNewSponsorsToAdd([]);
      },
      [onAddSponsors]
    );

    const sponsorContent = useMemo(() => {
      return sponsor ? (
        <UserCard
          user={sponsor}
          isSponsor={true}
          isEditMode={isEditMode}
          isSelected={isUserSelected(sponsor)}
          onSelectionChange={handleUserSelection}
        />
      ) : (
        <EmptyState icon={Crown} title="Cet utilisateur n'a pas de parrain" />
      );
    }, [sponsor, isEditMode, isUserSelected, handleUserSelection]);

    const deleteButton = useMemo(() => {
      if (!isEditMode || selectedSponsors.length === 0) return null;

      return (
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDeleteSelected}
          className="flex gap-2 items-center"
        >
          <Trash2 className="w-4 h-4" />
          Supprimer ({selectedSponsors.length})
        </Button>
      );
    }, [isEditMode, selectedSponsors.length, handleDeleteSelected]);

    const searchSection = useMemo(() => {
      if (!isEditMode) return null;

      return (
        <div className="p-4 mt-4 bg-gray-50 rounded border">
          <UserSearchAdd
            onAddUsers={handleAddNewSponsors}
            selectedUsers={newSponsorsToAdd}
            excludeUsers={sponsor ? [sponsor] : []}
            placeholder="Rechercher un parrain..."
            maxSelection={1}
            sectionTitle="Ajouter un parrain"
          />
        </div>
      );
    }, [isEditMode, sponsor, newSponsorsToAdd, handleAddNewSponsors]);

    const sectionHeader = useMemo(() => {
      return (
        <div className="flex justify-between items-center mb-3">
          <div className="flex gap-2 items-center">
            <Crown className="w-5 h-5 text-yellow-500" />
            <h3 className="text-lg font-semibold">Parrain</h3>
          </div>
          {deleteButton}
        </div>
      );
    }, [deleteButton]);

    return (
      <div>
        {sectionHeader}
        {sponsorContent}
        {searchSection}
      </div>
    );
  }
);

SponsorSection.displayName = "SponsorSection";

export default SponsorSection;
